import { useEffect, useState } from 'react';
import { LiveBreakdownItem } from '../lib/api';
import { useDashboard, useRealtime, useLastUpdated, useAdminRefresh } from '../context/AdminPollingContext';

interface QuickStat {
  label: string; value: number | string; icon: string; color: string; sub?: string;
}

interface InboxStats { total: number; active: number; total_messages: number; }
interface BlogStats { total: number; published: number; draft: number; }

const PAGE_TYPE_LABELS: Record<string, { label: string; emoji: string }> = {
  home:    { label: 'Home / Inbox', emoji: '🏠' },
  blog:    { label: 'Blog',         emoji: '📖' },
  guide:   { label: 'Guides',       emoji: '🗺️' },
  faq:     { label: 'FAQ',          emoji: '❓' },
  about:   { label: 'About',        emoji: 'ℹ️' },
  contact: { label: 'Contact',      emoji: '✉️' },
  privacy: { label: 'Privacy',      emoji: '🔒' },
  terms:   { label: 'Terms',        emoji: '📜' },
  other:   { label: 'Other',        emoji: '🔗' },
};

const LANG_FLAGS: Record<string, string> = {
  en: '🇬🇧', tr: '🇹🇷', de: '🇩🇪', fr: '🇫🇷',
  id: '🇮🇩', pl: '🇵🇱', pt: '🇵🇹', ar: '🇸🇦', hi: '🇮🇳', ur: '🇵🇰',
};

export default function Dashboard() {
  const [stats, setStats] = useState<QuickStat[]>([]);
  const [liveBreakdown, setLiveBreakdown] = useState<LiveBreakdownItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { dashboard, loading: dashLoading } = useDashboard();
  const realtime = useRealtime();
  const lastUpdated = useLastUpdated();
  const refresh = useAdminRefresh();

  useEffect(() => {
    if (dashboard) {
      const { counts: c, uniqueVisitors: uv, inboxStats: is, blogStats: bs } = dashboard;
      // Only metrics that actually move. "Sessions" (dead since Oct 2026) and
      // "API Calls" (no API keys exist) were permanently 0 and got removed.
      setStats([
        { label: 'Messages Received', value: realtime.messages24h, icon: '📥', color: 'from-blue-500 to-blue-600', sub: 'last 24h' },
        { label: 'Inboxes Created',   value: c.email,     icon: '📬', color: 'from-emerald-500 to-emerald-600', sub: 'last 24h' },
        { label: 'Site Visits',       value: c.visit,     icon: '👁️', color: 'from-green-500 to-green-600',  sub: 'last 24h' },
        { label: 'Unique Visitors',   value: uv,          icon: '🙋', color: 'from-teal-500 to-teal-600',    sub: 'last 24h' },
        { label: 'Total Visits',      value: c.allVisits, icon: '🌐', color: 'from-cyan-500 to-cyan-600',    sub: 'all time' },
        { label: 'Blog Post Views',   value: c.postVisit, icon: '📖', color: 'from-orange-500 to-orange-600', sub: 'last 24h' },
        { label: 'Blog Posts',        value: bs.total,    icon: '✍️', color: 'from-rose-500 to-rose-600',     sub: `${bs.published} published · ${bs.draft} draft` },
        { label: 'Active Inboxes',    value: is.active,   icon: '📮', color: 'from-purple-500 to-purple-600', sub: 'right now' },
        { label: 'Total Inboxes',     value: is.total,    icon: '📦', color: 'from-indigo-500 to-indigo-600', sub: 'all time' },
        { label: 'Total Messages',    value: is.total_messages, icon: '✉️', color: 'from-pink-500 to-pink-600', sub: 'all time' },
      ]);
      setLiveBreakdown(dashboard.realtime.liveBreakdown || []);
      setLoading(false);
    }
  }, [dashboard, realtime.messages24h]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Dashboard</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-xl px-4 py-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
              {realtime.activeNow} live now
            </span>
          </div>
          <button onClick={() => refresh()} disabled={dashLoading} className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 transition">
            {dashLoading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Realtime mini-stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Live now', value: realtime.activeNow, color: 'text-emerald-600 dark:text-emerald-400' },
          { label: 'Messages received (24h)', value: realtime.messages24h, color: 'text-blue-600 dark:text-blue-400' },
          { label: 'Inboxes created (24h)', value: realtime.emailsToday, color: 'text-purple-600 dark:text-purple-400' },
          { label: 'Total visits', value: realtime.totalVisits, color: 'text-amber-600 dark:text-amber-400' },
        ].map(s => (
          <div key={s.label} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow p-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Live users breakdown — who's on which page/language right now */}
      <div className="mb-8 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Live visitors — where they are right now</h2>
          <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
            Live sessions expire after ~5 min · refresh for new data
          </span>
        </div>

        {liveBreakdown.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
            No active visitors right now
          </p>
        ) : (
          <div className="space-y-2">
            {liveBreakdown.map((item) => {
              const meta = PAGE_TYPE_LABELS[item.pageType] ?? { label: item.pageType, emoji: '🔗' };
              const flag = LANG_FLAGS[item.lang] ?? '🌐';
              const maxCount = liveBreakdown[0].count;
              const pct = Math.round((item.count / maxCount) * 100);
              return (
                <div key={`${item.pageType}:${item.lang}`} className="flex items-center gap-3">
                  <span className="text-base w-5 text-center">{meta.emoji}</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200 w-28 flex-shrink-0">
                    {meta.label}
                  </span>
                  <span className="text-sm text-gray-400 dark:text-gray-500 w-8 flex-shrink-0">
                    {flag} {item.lang}
                  </span>
                  <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 w-6 text-right flex-shrink-0">
                    {item.count}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Main stat cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-gray-200/60 dark:bg-gray-700/40 animate-pulse h-32" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {stats.map(({ label, value, icon, color, sub }) => (
            <div key={label}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${color} p-6 text-white shadow-xl transform transition-transform hover:scale-[1.02]`}>
              <div className="absolute top-0 right-0 text-5xl opacity-20 -mt-2 -mr-2">{icon}</div>
              <h3 className="text-sm font-medium opacity-90">{label}</h3>
              <p className="text-4xl font-bold mt-2">{typeof value === 'number' ? value.toLocaleString() : value}</p>
              {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Quick links */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'View Analytics', href: '/analytics', emoji: '📊' },
          { label: 'Manage Inboxes', href: '/inboxes', emoji: '📬' },
          { label: 'Blog & Guides', href: '/blog', emoji: '✍️' },
          { label: 'Own Mail', href: '/own-mail', emoji: '📨' },
        ].map(l => (
          <a key={l.href} href={l.href}
            className="flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-2xl px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 hover:shadow-md transition">
            <span>{l.emoji}</span> {l.label}
          </a>
        ))}
      </div>
    </div>
  );
}
